import React, { useEffect, useMemo, useRef } from "react";
import { styled, css, ThemeProvider } from "styled-components";
import Colors from "../../styles/Colors";
import { Label } from "../Text";
import rem from "../../utils/rem";
import srOnly from "../../utils/srOnly";
const buttonThemes = {
    primary: {
        labelColor: Colors.WHITE_2024,
        backgroundColor: Colors.PROFILE_RED_2024,
        disabledBackgroundColor: Colors.GRAY_LIGHT_DISABLED,
        focusBackground: Colors.PROFILE_RED_2024,
        hoverBackground: Colors.PROFILE_RED_2024,
        border: "1px solid transparent",
        disabledLabelColor: Colors.GRAY_DARK,
        hoverColor: Colors.WHITE_2024,
    },
    secondary: {
        labelColor: Colors.BLACK,
        backgroundColor: Colors.GRAY_LIGHT,
        disabledBackgroundColor: Colors.GRAY_LIGHT_DISABLED,
        border: "1px solid transparent",
        disabledLabelColor: Colors.GRAY_DARK,
        focusBackground: Colors.PROFILE_RED_2024,
        hoverBackground: Colors.PROFILE_RED_2024,
        hoverColor: Colors.WHITE_2024,
    },
    ghost: {
        labelColor: Colors.BLACK,
        backgroundColor: Colors.WHITE,
        disabledBackgroundColor: Colors.GRAY_LIGHT_DISABLED,
        border: `1px solid ${Colors.PROFILE_RED_2024}`,
        hoverBackground: Colors.PROFILE_RED_2024,
        hoverBorder: `1px solid ${Colors.PROFILE_RED_2024}`,
        disabledBorder: "1px solid transparent",
        disabledLabelColor: Colors.GRAY_DARK,
        hoverColor: Colors.WHITE_2024,
    },
    warning: {
        labelColor: Colors.ALERT_ERROR,
        backgroundColor: Colors.ALERT_ERROR_LIGHT,
        disabledBackgroundColor: Colors.ALERT_ERROR_LIGHT,
        border: "1px solid transparent",
        disabledLabelColor: Colors.ALERT_ERROR,
    },
    clean: {
        labelColor: Colors.BLACK,
        backgroundColor: Colors.WHITE,
        disabledBackgroundColor: Colors.GRAY_LIGHT_DISABLED,
        focusBackground: Colors.GRAY_LIGHT,
        hoverBackground: Colors.GRAY_LIGHT,
        hoverColor: Colors.BLACK,
        hoverBorder: "1px solid transparent",
        disabledBorder: "1px solid transparent",
        disabledLabelColor: Colors.GRAY_LIGHT_DISABLED,
        border: "1px solid transparent",
    },
};
const buttonSizes = {
    small: {
        key: "small",
        fontSize: rem(14),
        height: "2rem",
        padding: "1px 1rem 0",
        iconButtonSize: 40,
        iconSize: {
            width: 20,
            height: 20,
        },
    },
    medium: {
        key: "medium",
        fontSize: rem(16),
        height: "2.5rem",
        padding: "2px 1.5rem 0",
        iconButtonSize: 40,
        iconSize: {
            width: 20,
            height: 20,
        },
    },
    large: {
        key: "large",
        fontSize: rem(16),
        height: "3rem",
        padding: "2px 1.5rem 0",
        iconButtonSize: 40,
        iconSize: {
            width: 20,
            height: 20,
        },
    },
};
const StyledButton = styled.button `
  ${({ isLink }) => isLink &&
    css `
      display: inline-flex;
      align-items: center;
      justify-content: center;
    `}

  ${({ isSpan }) => isSpan &&
    css `
      display: inline-flex;
      align-items: center;
      justify-content: center;
    `}

  transition: all .2s ease-out;
  box-sizing: border-box;
  appearance: none;
  border: ${(props) => props.theme.vars.border};

  ${({ dashedBorder }) => dashedBorder &&
    css `
      border-style: dashed;
    `}

  border-radius: ${({ theme }) => (theme.isIconButton ? "100%" : rem(10))};
  cursor: pointer;
  margin: 0;
  min-height: ${({ theme }) => theme.isIconButton ? rem(theme.size.iconButtonSize) : theme.size.height};

  padding: ${({ theme }) => (theme.isIconButton ? 0 : theme.size.padding)};
  background: ${(props) => props.theme.vars.backgroundColor};
  ${({ theme }) => theme.isIconButton &&
    css `
      width: ${rem(theme.size.iconButtonSize)};
    `}

  &:hover {
    transition: all 0.2s ease-out;
     background: ${(props) => props.theme.vars.hoverBackground};
    ${({ dashedBorder }) => dashedBorder &&
    css `
        border-color: transparent;
      `}
  }

  &:focus {
    ${({ theme }) => theme.vars.focusBackground &&
    css `
        // background: ${theme.vars.focusBackground};
      `}
  }

  &:disabled {
    box-shadow: none;
    outline: none;
    background: ${(props) => props.theme.vars.disabledBackgroundColor};
    ${({ theme }) => theme.vars.disabledBorder &&
    css `
        border: ${theme.vars.disabledBorder};
      `}
    cursor: not-allowed;

    > svg {
      stroke: ${(props) => props.theme.vars.disabledLabelColor};
      fill: ${(props) => props.theme.vars.disabledLabelColor};
    }
  }
  &:hover > span > span {
  ${(props) => !props.disabled && `
    color: ${props.theme.vars.hoverColor} !important;
  `}

  &.trailing, &.leading {
      > svg {
        fill: ${(props) => props.theme.vars.hoverColor};
      }
}
`;
const ButtonWrapper = styled.span `
  display: flex;
  align-items: center;
  justify-content: center;
  & > span {
    font-size: ${(props) => props.theme.size.fontSize};
    color: ${(props) => props.theme.vars.labelColor};
    line-height: ${(props) => rem(props.theme.size.iconSize.height)};
    position: relative;
    bottom: -1px;
    ${({ isDisabled }) => isDisabled &&
    css `
        color: ${(props) => props.theme.vars.disabledLabelColor};
      `}
    ${(props) => props.theme.isIconButton && srOnly}
  }
  & > span.iconWrap {
    position: relative;
    bottom: 0;
    width: auto;
    height: auto;
    display: flex;
    &.leading {
      > svg {
        margin-right: ${(props) => (props.theme.isIconButton ? 0 : rem(12))};
        fill: ${(props) => props.theme.vars.labelColor};
      }
    }
    &.trailing {
      > svg {
        margin-left: ${(props) => (props.theme.isIconButton ? 0 : rem(12))};
        fill: ${(props) => props.theme.vars.labelColor};
      }
    }
    > svg {
      margin-top: ${({ theme }) => theme.size.key === "small" && !theme.isIconButton ? "-2px" : "-1px"};
    }
  }

  & .btn-standard-text,
  & .btn-animated-text {
    transition: opacity 0.5s linear;
  }

  & .btn-standard-text {
    ${(props) => props.animated &&
    css `
        opacity: 0;
        display: none;
      `}
  }
  & .btn-animated-text {
    opacity: 0;
    position: absolute;
    left: 0;

    ${(props) => props.animated &&
    css `
        opacity: 1;
        position: relative;
      `}
  }
`;
/**
 * Button component to be used for all button elements.
 *
 * ## Button guidelines
 * - Adjust the buttons on the left (in the form: in line with the input)
 * - Back link / button is placed above the form
 * - Other actions and links such as "log in with email instead of mobile number" are added above the form
 * - If more actions than one primary are required, select secondary or ghost buttons and place them to the right of the primary button. The secondary buttons should break below if there is not enough space for secondary buttons.
 * - In dialogues ( such as in gruppebooking etc.) one should align primary action to the right as this follows traditional western reading flow for what is "next"
 */
export const Button = React.memo(({ children, icon, trailingIcon, leadingIcon, title, size = "small", theme = "primary", renderAs = "button", noBorder, animation, dashedBorder = false, dataCy, ...props }) => {
    const buttonRef = useRef(null);
    const LeadingIcon = icon || leadingIcon;
    const TrailingIcon = trailingIcon;
    const hasChildren = React.Children.count(children) > 0;
    const isIconButton = !hasChildren;
    const themeVars = buttonThemes[theme];
    const buttonSizesForSize = buttonSizes[size];
    const buttonAnimation = useMemo(() => (animation ? { finishTimeout: 3000, ...animation } : null), [animation]);
    useEffect(() => {
        if (buttonAnimation?.animate) {
            setTimeout(() => {
                if (buttonAnimation?.onFinishAnimate) {
                    // Run on finish animate to reset animation
                    buttonAnimation.onFinishAnimate();
                    // Remove focus
                    buttonRef.current?.blur();
                }
            }, buttonAnimation.finishTimeout);
        }
    }, [buttonAnimation, buttonAnimation?.animate]);
    return (React.createElement(ThemeProvider, { theme: {
            vars: themeVars,
            size: buttonSizesForSize,
            hasIcon: !!(LeadingIcon || TrailingIcon),
            isIconButton,
            noBorder,
        } },
        React.createElement(StyledButton, { ref: buttonRef, title: title, as: props.href ? "a" : renderAs, ...props, isLink: !!props.href, isSpan: renderAs === "span", dashedBorder: dashedBorder, "data-cy": dataCy },
            React.createElement(ButtonWrapper, { animated: buttonAnimation?.animate, isDisabled: props.disabled },
                LeadingIcon && (React.createElement("span", { className: "iconWrap leading" },
                    React.createElement(LeadingIcon, { width: buttonSizesForSize.iconSize.width, height: buttonSizesForSize.iconSize.height }))),
                (hasChildren || title) && (React.createElement(Label, { as: "span" },
                    React.createElement("span", { className: "btn-standard-text" }, hasChildren ? children : title),
                    buttonAnimation?.text && (React.createElement("span", { className: "btn-animated-text" }, buttonAnimation?.text)))),
                TrailingIcon && (React.createElement("span", { className: "iconWrap trailing" },
                    React.createElement(TrailingIcon, { width: buttonSizesForSize.iconSize.width, height: buttonSizesForSize.iconSize.height })))))));
});
Button.displayName = "Button";
export default Button;
//# sourceMappingURL=Button.js.map